/**
 * "Containers and jobs" (docs section 3): three bands from top to bottom. The edge, the
 * Container Apps environment (two apps, six jobs with their triggers, the managed identity
 * tags on each box) and the stores. On a phone every band is one column (narrow query).
 */

import {
  ArrowDown,
  Database,
  DoorOpen,
  Icon,
  type IconComponent,
  IdCard,
  KeyRound,
  Monitor,
  Package,
  ScrollText,
} from "@norican/icons";
import { Badge, Card, Typography } from "@norican/react";
import type { ReactNode } from "react";

import { NARROW_QUERY, useMediaQuery } from "@/hooks/use-media-query";

import { Figure } from "./figure";

export const CONTAINERS_AND_JOBS_TITLE = "Containers and jobs";
/** What the drawing shows, for a screen reader. */
export const CONTAINERS_AND_JOBS_SUMMARY =
  "Three bands: the edge, the Container Apps environment with two apps and six jobs, and the stores.";

/** A box outside the environment: an icon, a name and one line (backticks mark code). */
type Part = readonly [icon: IconComponent, name: string, note: string];
/** A container app or a job: its name, its port or trigger, a tag, its identity tags. */
type Unit = { name: string; line: string; tag: string; ids: string };

const EDGE: readonly Part[] = [
  [Monitor, "Browser", "A person signs in with Entra."],
  [DoorOpen, "Front Door", "`RFQ_REQUIRE_FDID` locks the frontend to it."],
];

const APPS: readonly Unit[] = [
  { name: "frontend", line: "nginx on port 3000", tag: "public", ids: "" },
  { name: "backend", line: "FastAPI on port 8000", tag: "internal", ids: "app powerbi fo" },
];

/** The six jobs. The tag says where the job is defined. */
export const CONTAINER_JOBS: readonly Unit[] = [
  { name: "mail-ingest", line: "Every 5 minutes", tag: "Terraform", ids: "app powerbi fo" },
  { name: "imdp-sync", line: "Hourly, at minute 17", tag: "Script", ids: "app imdp" },
  { name: "oa-sync", line: "Hourly, at minute 47", tag: "Script", ids: "app powerbi" },
  { name: "crm-sync", line: "Nightly, at 03:23 UTC", tag: "Script", ids: "app powerbi" },
  { name: "migrate", line: "Manual: the deploy starts it", tag: "Terraform", ids: "app" },
  { name: "loader", line: "Manual: DDL as `appadmin`", tag: "Not in the repo", ids: "" },
];

/** The identity tags: the short tag, then the name in Azure and what the identity reads. */
export const CONTAINER_IDENTITIES: readonly (readonly [string, string])[] = [
  ["app", "`rfq-foundry-app-<env>` reads Key Vault and Graph `Mail.Read`."],
  ["powerbi", "`id-rfq-powerbi-read-dev` reads OmniAnalytics."],
  ["fo", "`id-rfq-fo-read-dev` reads F&O through OData."],
  ["imdp", "`id-shared-imdp-prd-ng-admin` is the one identity that IMDP accepts."],
];

const STORES: readonly Part[] = [
  [Database, "Postgres flexible server", "Private access. App DB `rfq_foundry`."],
  [Package, "Container registry", "The images: backend, frontend, `imdp-sync`."],
  [KeyRound, "Key Vault", "The secrets. The app identity has Get and List."],
  [ScrollText, "Log Analytics", "The logs. The alerts read them."],
];

const COLUMN = "flex flex-col gap-2";
const GRID = { 2: "grid grid-cols-2 gap-2", 3: "grid grid-cols-3 gap-2" };
const BOX = "flex min-w-0 flex-col gap-0.5";

/** Backtick spans in code font, as the documentation page prints them. */
function code(text: string): ReactNode {
  return text.split("`").map((part, index) =>
    index % 2 ? (
      <Typography key={index} as="code" variant="code" className="text-[0.9em] break-words">
        {part}
      </Typography>
    ) : (
      part
    ),
  );
}

function PartBox({ part: [icon, name, note] }: { part: Part }) {
  return (
    <Card variant="outline" padding="sm" className={BOX}>
      <span className="flex min-w-0 items-center gap-1.5">
        <Icon icon={icon} size="sm" className="text-fg-muted shrink-0" />
        <Typography variant="label">{name}</Typography>
      </span>
      <Typography variant="caption" tone="muted" className="break-words">
        {code(note)}
      </Typography>
    </Card>
  );
}

function UnitBox({ unit }: { unit: Unit }) {
  return (
    <Card variant="outline" padding="sm" accent="left" className={BOX}>
      <span className="flex min-w-0 flex-wrap items-center justify-between gap-1">
        <Typography as="code" variant="code" className="font-semibold break-words">
          {unit.name}
        </Typography>
        <Badge tone="neutral" variant="outline" size="sm">
          {unit.tag}
        </Badge>
      </span>
      <Typography variant="body-sm">{code(unit.line)}</Typography>
      <span className="flex flex-wrap items-center gap-1">
        {unit.ids.split(" ").map((id) => id && <IdentityTag key={id} id={id} />)}
      </span>
    </Card>
  );
}

function IdentityTag({ id }: { id: string }) {
  return (
    <Badge tone="accent" variant="subtle" size="sm">
      <Icon icon={IdCard} size="xs" />
      {id}
    </Badge>
  );
}

/** A band: an arrow down (bands two and three), the band name, then the boxes. */
function Band({ name, arrow, children }: { name: string; arrow?: string; children: ReactNode }) {
  return (
    <li className="flex flex-col gap-1">
      {arrow && (
        <span className="text-fg-muted flex items-center justify-center gap-1 text-xs">
          <Icon icon={ArrowDown} size="sm" label="to" />
          {arrow}
        </span>
      )}
      <Card variant="outline" padding="sm" className={COLUMN}>
        <Typography variant="heading-6" as="p">
          {name}
        </Typography>
        {children}
      </Card>
    </li>
  );
}

/** `layout` overrides the media query for the render tests. Omit it in the app. */
export function ContainersAndJobsFigure(props: { caption?: string; layout?: "bands" | "stacked" }) {
  const narrow = useMediaQuery(NARROW_QUERY);
  const form = props.layout ?? (narrow ? "stacked" : "bands");
  const grid = (columns: 2 | 3) => (form === "stacked" ? COLUMN : GRID[columns]);
  const parts = (list: readonly Part[]) => (
    <div className={grid(2)}>
      {list.map((part) => (
        <PartBox key={part[1]} part={part} />
      ))}
    </div>
  );
  const units = (label: string, list: readonly Unit[], columns: 2 | 3) => (
    <>
      <Typography variant="caption" tone="muted">
        {code(label)}
      </Typography>
      <div className={grid(columns)}>
        {list.map((unit) => (
          <UnitBox key={unit.name} unit={unit} />
        ))}
      </div>
    </>
  );
  return (
    <Figure name="containers-and-jobs" title={CONTAINERS_AND_JOBS_TITLE} caption={props.caption}>
      <ol aria-label={CONTAINERS_AND_JOBS_SUMMARY} data-layout={form} className={COLUMN}>
        <Band name="Edge">{parts(EDGE)}</Band>
        <Band name="Container Apps environment" arrow="HTTPS to the frontend only">
          {units("Apps. nginx in the frontend sends `/api/` to the backend.", APPS, 2)}
          {units("Jobs. The full name of a job is `rfq-foundry-<job>-<env>`.", CONTAINER_JOBS, 3)}
          <Typography variant="caption" tone="muted">
            Managed identities. A tag on a box means that the identity is attached to it.
          </Typography>
          <ul className={grid(2)}>
            {CONTAINER_IDENTITIES.map(([id, text]) => (
              <li key={id} className="flex min-w-0 items-start gap-1.5 text-xs">
                <IdentityTag id={id} />
                <span className="min-w-0 break-words">{code(text)}</span>
              </li>
            ))}
          </ul>
        </Band>
        <Band name="Stores and services" arrow="private network, secrets by reference">
          {parts(STORES)}
        </Band>
      </ol>
    </Figure>
  );
}
